<script setup lang="ts">
// The app frame, Sierra's shell (ADR 0010). From 721 px it's a 92 px rail: the brand,
// icon-over-label nav, a spacer, and the foot (the inbox, the user menu, the
// connection status). At 720 px and below the nav becomes a bottom tab bar, and a slim
// header carries the brand and the foot. Each slot is rendered once and moved by grid
// areas, so nothing in the foot is mounted twice. `main` is the only scroller.
// Navigation and the menus come in through slots, so @itc/ui needs no router.
withDefaults(defineProps<{ appName: string; appSub?: string; watermark?: string }>(), { appSub: '' });
</script>

<template>
  <div class="itc-shell">
    <div class="itc-shell__brand">
      <slot name="brand">
        <span class="itc-shell__wordmark">{{ appName }}<small v-if="appSub">{{ appSub }}</small></span>
      </slot>
    </div>
    <nav class="itc-rail" aria-label="Main"><slot name="nav" /></nav>
    <div class="itc-shell__foot"><slot name="foot" /></div>
    <main class="itc-shell__main" :class="{ 'itc-shell__main--watermark': watermark }" :style="watermark ? { '--shell-watermark': `url(${watermark})` } : undefined">
      <slot />
    </main>
  </div>
</template>

<style scoped>
.itc-shell {
  display: grid;
  grid-template-columns: var(--itc-rail-width) minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr) auto;
  grid-template-areas:
    'brand main'
    'nav   main'
    'foot  main';
  height: 100vh;
  height: 100dvh;
}
.itc-shell__brand,
.itc-rail,
.itc-shell__foot {
  background: var(--itc-panel);
  border-right: 1px solid var(--itc-line);
}
.itc-shell__brand {
  grid-area: brand;
  padding: 10px 8px 0;
}
.itc-shell__wordmark {
  display: block;
  padding: 4px 0 10px;
  border-bottom: 1px solid var(--itc-line);
  color: var(--itc-accent);
  font-family: var(--itc-font-display);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.14em;
  line-height: 1.1;
  text-align: center;
  text-transform: uppercase;
}
.itc-shell__wordmark small {
  display: block;
  margin-top: 3px;
  color: var(--itc-muted);
  font-size: 9.5px;
  letter-spacing: 0.16em;
}
.itc-rail {
  grid-area: nav;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 0;
  padding: 6px 8px;
  overflow-y: auto;
}
.itc-shell__foot {
  grid-area: foot;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 8px 8px 10px;
}
.itc-shell__main {
  grid-area: main;
  position: relative;
  isolation: isolate;
  min-width: 0;
  overflow-y: auto;
  padding: 20px 24px 48px;
}
.itc-shell__main--watermark::before {
  content: '';
  position: fixed;
  top: 50%;
  left: calc(50% + var(--itc-rail-width) / 2);
  z-index: -1;
  width: min(56vw, 520px);
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  background: var(--shell-watermark) center / contain no-repeat;
  opacity: 0.07;
  pointer-events: none;
}

@media (max-width: 720px) {
  .itc-shell {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: var(--itc-bar-height) minmax(0, 1fr) auto;
    grid-template-areas:
      'brand foot'
      'main  main'
      'nav   nav';
  }
  .itc-shell__brand,
  .itc-shell__foot {
    display: flex;
    align-items: center;
    border-right: 0;
    border-bottom: 1px solid var(--itc-line);
  }
  .itc-shell__brand {
    padding: 0 16px;
  }
  .itc-shell__wordmark {
    padding: 0;
    border-bottom: 0;
    text-align: left;
  }
  .itc-shell__wordmark small {
    display: none;
  }
  .itc-shell__foot {
    flex-direction: row;
    padding: 0 12px 0 0;
  }
  .itc-rail {
    flex-direction: row;
    padding: 4px 6px calc(4px + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--itc-line);
    border-right: 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
  .itc-shell__main {
    padding-inline: 16px;
  }
  .itc-shell__main--watermark::before {
    top: 50%;
    left: 50%;
    width: min(80vw, 420px);
  }
}
</style>
